import React from 'react';
import type { SurveySummaryQuestion } from '../../types/survey.types';

interface ScaleResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const ScaleResponse: React.FC<ScaleResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, answered_count, options } = question;
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Calculate average score
    const totalScore = options.reduce((sum, option) => {
        const score = parseInt(option.label);
        return sum + (score * option.count);
    }, 0);
    const averageScore = answered_count > 0 ? (totalScore / answered_count).toFixed(1) : '0.0';
    
    // Determine scale range from options
    const scores = options.map(option => parseInt(option.label)).sort((a, b) => b - a);
    const maxScale = Math.max(...scores);
    const minScale = Math.min(...scores);
    
    // Create a map of score to count for easy lookup
    const scoreCountMap = options.reduce((map, option) => {
        map[parseInt(option.label)] = option.count;
        return map;
    }, {} as Record<number, number>);
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-3">
                    {/* Question Icon */}
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                        <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.09961 3.34009V16.6734C3.09961 17.1154 3.2752 17.5394 3.58776 17.8519C3.90033 18.1645 4.32425 18.3401 4.76628 18.3401H18.0996" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M15.5996 15.0068V8.34009" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M11.4326 15.0068V5.00684" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M7.2666 15.0068V12.5068" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {position}. {title}
                            </h3>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <span className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-full border dark:border-gray-600">
                                Scale
                            </span>
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                {answered_count} of {totalResponses} responses
                            </span>
                            <span className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                                {responseRate}% response rate
                            </span>
                        </div>
                    </div>
                </div>
                
                {/* Average Score */}
                <div className="text-right">
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        <span className="font-bold">{averageScore}</span> / {maxScale} average
                    </span>
                </div>
            </div>

            {/* Scale Visualization */}
            <div className="space-y-4">
                {/* Scale Numbers and Bars */}
                <div className="flex items-end gap-3">
                    {Array.from({ length: maxScale - minScale + 1 }, (_, i) => {
                        const score = maxScale - i; // Display from highest to lowest
                        const count = scoreCountMap[score] || 0;
                        const maxCount = Math.max(...Object.values(scoreCountMap));
                        const barHeight = maxCount > 0 ? (count / maxCount) * 100 : 0;
                        
                        return (
                            <div key={score} className="flex flex-col items-center gap-2 flex-1 min-w-0">
                                {/* Scale Number */}
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {score}
                                </span>
                                
                                {/* Bar Container */}
                                <div className="w-full h-24 bg-gray-200 dark:bg-gray-600 rounded-sm relative">
                                    {/* Response Bar */}
                                    {count > 0 && (
                                        <div 
                                            className="absolute bottom-0 w-full rounded-sm transition-all duration-300 ease-in-out bg-[#00C49A] dark:bg-emerald-500"
                                            style={{ 
                                                height: `${barHeight}%`
                                            }}
                                        />
                                    )}
                                </div>
                                
                                {/* Count */}
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {count}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ScaleResponse;
